import { plural } from './approvalWords';

/**
 * The New site card's words (design 2026-10-27, "New Site From a Template"
 * §3): what was filled in for the person and where, and the placeholders
 * still on the pages, from the apiserver's tinycmsStarterReview. Kept apart
 * from the card so they're tested on their own.
 */

export interface StarterPlaces {
  labels: string[];
  count: number;
}

export interface StarterReview {
  template: { id: string; name: string };
  noun: string;
  filled: {
    name: string;
    line: string;
    lineGiven: boolean;
    ownerName: string | null;
    email: string | null;
    url: string | null;
    year: string;
    places: Partial<Record<'name' | 'line' | 'owner' | 'email', StarterPlaces>>;
  };
  placeholders: Array<{
    group: 'about' | 'examples' | 'photos' | 'contact';
    count: number;
    pages: Array<{ id: string; title: string | null; path: string; block: number }>;
  }>;
  pageCount: number;
  /** The site has a cover image for link previews. */
  shareImage?: boolean;
}

export interface NewSiteRow {
  value: string;
  where: string;
  /** Change / Edit: where it opens (none for the address and year). */
  to: string | null;
}

/** Where the site's name and line are changed, and the person's name and email. */
export const SITE_IDENTITY = '/content?view=manage&area=site#identity';
export const PROFILE = '/profile';

/** "header, browser title, footer, About page title and 2 more". */
function placesList(labels: string[]) {
  if (labels.length <= 5) return labels.join(', ');
  return `${labels.slice(0, 4).join(', ')} and ${labels.length - 4} more`;
}

const where = (lead: string, p: StarterPlaces | undefined) =>
  p && p.count ? `${lead} · ${placesList(p.labels)} (${plural(p.count, 'place')})` : lead;

/** FILLED IN FOR YOU: the site name, the line, the owner's name and email, the address and year. */
export function filledRows(r: StarterReview): NewSiteRow[] {
  const f = r.filled;
  const rows: NewSiteRow[] = [
    { value: f.name, where: where('Site name', f.places.name), to: SITE_IDENTITY },
  ];
  rows.push({
    value: f.line,
    where: f.lineGiven
      ? where('Your line from signup', f.places.line)
      : `No line given, so the home page leads with your ${f.ownerName ? 'name' : 'site name'} · ${placesList(
          f.places.line?.labels || ['home page headline']
        )}`,
    to: SITE_IDENTITY,
  });
  if (f.ownerName && f.places.owner?.count) {
    rows.push({ value: f.ownerName, where: where('Your name', f.places.owner), to: PROFILE });
  }
  if (f.email && f.places.email?.count) {
    rows.push({ value: f.email, where: where('Your account email', f.places.email), to: PROFILE });
  }
  rows.push({
    value: [f.url, f.year].filter(Boolean).join(' · '),
    where: f.url ? 'Address on every link · year in the footer' : 'Year in the footer',
    to: null,
  });
  return rows;
}

/** "5 things, 15 places" (the year in the footer is one place). */
export function filledCount(r: StarterReview, rows = filledRows(r)) {
  const p = r.filled.places;
  const places =
    (p.name?.count || 0) +
    (p.line?.count || 0) +
    (r.filled.ownerName ? p.owner?.count || 0 : 0) +
    (r.filled.email ? p.email?.count || 0 : 0) +
    1;
  return `${plural(rows.length, 'thing')}, ${plural(places, 'place')}`;
}

/** "story" → "stories", "dish" → "dishes". */
export function nouns(noun: string, n: number) {
  if (n === 1) return noun;
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  if (/(s|sh|ch|x)$/.test(noun)) return `${noun}es`;
  return `${noun}s`;
}

const pageNames = (pages: StarterReview['placeholders'][number]['pages']) =>
  pages.map((p) => p.title || p.path).join(', ');

/** Where Edit opens: the page in Content → Pages, scrolled to the group's first block. */
export const editHref = (page: { id: string; block: number }) =>
  `/content?view=pages&page=${encodeURIComponent(page.id)}&block=${page.block}`;

/** CHANGE WHENEVER YOU LIKE: one row per placeholder group still on the pages. */
export function placeholderRows(r: StarterReview): NewSiteRow[] {
  const site = r.filled.name;
  return r.placeholders.map((g) => {
    const first = g.pages[0];
    const to = first ? editHref(first) : null;
    // The About page itself (not the home page's short "About" band) names the row.
    const aboutPage = g.pages.find((p) => p.path !== '/' && p.path !== '/home') || first;
    switch (g.group) {
      case 'about':
        return {
          value: `${aboutPage?.title || 'About'} page: ${
            g.count > 1 ? `${g.count} short paragraphs` : 'a short note'
          } about ${site}`,
          where: `${pageNames(g.pages)} · written from your ${
            r.filled.ownerName ? 'name' : 'site name'
          } and line`,
          to: aboutPage ? editHref(aboutPage) : to,
        };
      case 'examples':
        return {
          value: `${g.count} example ${nouns(r.noun, g.count)} with titles and blurbs`,
          where: `${pageNames(g.pages)} · each one says it’s an example`,
          to,
        };
      case 'photos':
        return {
          value: plural(g.count, 'photo spot'),
          where: `${pageNames(g.pages)} · neutral placeholders until you add photos`,
          to,
        };
      default:
        return {
          value: `${first?.title || 'Contact'} page intro`,
          where: `${first?.title || 'Contact'} · ${
            r.filled.email ? 'your email; ' : ''
          }no hours or address made up`,
          to,
        };
    }
  });
}

/** What the template's sample copy was about, for the note under the placeholders. */
const SAMPLE_BUSINESS: Record<string, string> = {
  studio: 'a studio',
  local: 'a shop',
  consultant: 'a practice',
  portfolio: 'a career',
  restaurant: 'a restaurant',
  product: 'a product',
  journal: 'a journal',
  magazine: 'a magazine',
  publisher: 'a newsroom',
  newsroom: 'a newsroom',
  nonprofit: 'a charity',
};

/** "Written from your site name and your line, so nothing on the site talks about a studio you don’t have. …" */
export function placeholderNote(r: StarterReview) {
  const what = SAMPLE_BUSINESS[r.template.id] || 'a business';
  return `Written from your site name and your line, so nothing on the site talks about ${what} you don’t have. It reads fine as is; swap in your own words and photos whenever you like.`;
}

export const placeholderCount = (rows: NewSiteRow[]) =>
  `${plural(rows.length, 'placeholder')}, marked on the page`;

/** What publishing does (the card's effect line). */
export function newSiteEffect(pageCount: number, host: string) {
  return `Publishing makes the header, footer and ${plural(pageCount, 'page')} public at ${host}. Everything stays editable.`;
}

/**
 * What the preview's review outlines look for (smagent-sites PreviewOutline,
 * ?outline=starter): the values that went in, in the address's #filled=.
 */
export function reviewHash(r: StarterReview) {
  const f = r.filled;
  const filled: Record<string, string> = {
    name: f.name,
    line: f.line,
    lineTag: f.lineGiven ? 'YOUR LINE' : f.ownerName ? 'FROM YOUR NAME' : 'FROM YOUR SITE NAME',
  };
  if (f.ownerName) filled.owner = f.ownerName;
  if (f.email) filled.email = f.email;
  return `#filled=${encodeURIComponent(JSON.stringify(filled))}`;
}
